import { ChevronRight, Star } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { useGoogleBusinessConnection } from "@/domains/settings/features/integrations/google-business/hooks/use-google-business-connection";
import type { SalonReviewSource } from "@/gql/graphql";
import { BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";
import { AwaitingReplySection } from "../components/awaiting-reply-section";
import { GbpInsightsSection } from "../components/gbp-insights-section";
import { PromoSection } from "../components/promo-section";
import { RequestReviewDialog } from "../components/request-review-dialog";
import { StarDistributionBar } from "../components/star-distribution-bar";
import { useSalonReviewStats } from "../hooks/use-salon-review-stats";

/**
 * Customer Service overview. The whole screen lives inside a single
 * page-level Card — sections are separated by horizontal rules and
 * headings rather than by being individual nested cards. Layout
 * patterned after the Customer Care Report design.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ Customer feedback                                            │
 *   │ ──────────┬─────────────────────┬───────────────────────────│
 *   │ 4.3 ★    │ Rating distribution │ By platform               │
 *   │ N total  │ 5 ▓▓▓▓▓ … 1 …       │ • Google Business 4.4 ★  │
 *   │          │                     │ • Platform        4.0 ★  │
 *   │ Request a review                              [Get link/QR]│
 *   └─────────────────────────────────────────────────────────────┘
 *     ⚠ Awaiting your reply (only when count > 0)
 *     [review row] [review row] …
 *
 * Customer Feedback is one read-only summary Card (Request a Review
 * is a sub-row inside it). Awaiting Reply renders below as a flat
 * section — its individual review rows still carry their own row-card
 * styling, so the list reads as actionable items under a section
 * header. "By platform" rows still navigate to the per-source detail
 * pages.
 */
export function ReviewsOverviewPage() {
  const { t } = useTranslation("reviews");
  const { currentSalon } = useSalonStore();
  const { stats: combinedStats, loading: combinedLoading } = useSalonReviewStats();

  if (!currentSalon) {
    return (
      <>
        <BaseNavigationBar title={t("overview.title")} subtitle={t("overview.subtitle")} />
        <PageContent>
          <div className="text-sm text-muted-foreground">{t("overview.selectSalon")}</div>
        </PageContent>
      </>
    );
  }

  return (
    <>
      <BaseNavigationBar title={t("overview.title")} subtitle={t("overview.subtitle")} />
      <PageContent>
        <div className="space-y-9">
          {/* WhatsApp promo re-entry — first section, only renders when there
              is an active promo with at least one image. */}
          <PromoSection />

          {/* GBP performance metrics — only renders when the salon has a
              connected Google Business profile. */}
          <GbpInsightsSection />

          {/* Customer Feedback header — outside the card so it reads
              as a section heading, matching the AwaitingReplySection style. */}
          <div className="pl-1.5 space-y-1">
            <h2 className="text-lg font-semibold">{t("overview.feedbackHeading")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("overview.feedbackSubheading")}
            </p>
          </div>

          <Card>
            <CardContent className="p-5">
              <CustomerFeedbackSection
                loading={combinedLoading && !combinedStats}
                averageRating={combinedStats?.averageRating ?? 0}
                count={combinedStats?.count ?? 0}
                distribution={combinedStats?.distribution ?? [0, 0, 0, 0, 0]}
              />
            </CardContent>
          </Card>

          {/* Service-recovery triage lives in its own Card — visually
              detached so the actionable surface reads as distinct from
              the read-only summary above. Hides itself when nothing
              needs attention. */}
          <AwaitingReplySection />
        </div>
      </PageContent>
    </>
  );
}

interface CustomerFeedbackSectionProps {
  loading: boolean;
  averageRating: number;
  count: number;
  distribution: number[];
}

/**
 * Bottom section in the page Card. Three-column stats block followed by
 * the Request-a-review action row — separated within the section by a
 * contained (non-edge-to-edge) divider so they read as related parts of
 * the same section rather than two different sections.
 *
 *   - Left: big average rating + total count
 *   - Middle: rating-distribution histogram (5★ → 1★)
 *   - Right: per-platform rows (Google Business, Platform)
 *   - Footer: Request-a-review row (link/QR generator)
 */
function CustomerFeedbackSection({
  loading,
  averageRating,
  count,
  distribution,
}: CustomerFeedbackSectionProps) {
  const { t } = useTranslation("reviews");

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="sm" />
        </div>
      ) : count === 0 ? (
        // Nothing to summarise yet — the rating headline, the histogram
        // and the per-platform rows would all render as empty scaffolding,
        // so the whole body is swapped for an empty state that explains
        // the blank and offers the one useful next action.
        <NoFeedbackPlaceholder />
      ) : (
        // Three columns on md+. The middle column gets the flexible
        // space so the histogram bars have room to breathe; left and
        // right columns are sized to their content.
        <div className="grid gap-6 md:grid-cols-[auto_1fr_minmax(220px,260px)]">
          {/* Left — headline rating */}
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold tabular-nums leading-none">
                {count ? averageRating.toFixed(1) : "—"}
              </span>
              <Star className="h-6 w-6 fill-amber-400 text-amber-400" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {count} {count === 1 ? t("overview.review") : t("overview.reviews")}{" "}
              {t("overview.totalSuffix")}
            </p>
          </div>

          {/* Middle — distribution histogram */}
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {t("overview.ratingDistribution")}
            </p>
            <StarDistributionBar distribution={distribution} />
          </div>

          {/* Right — by-platform breakdown (clickable rows) */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {t("overview.byPlatform")}
            </p>
            <div className="space-y-2">
              <GoogleBusinessPlatformRow />
              <PlatformRow
                source="MANUAL"
                label={t("overview.platformCardTitle")}
                href="/customer-service/platform"
                accent="indigo"
                hideWhenEmpty
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Google Business row in the "By platform" column.
 *
 * Split out from the generic `PlatformRow` because an unconnected salon
 * has no rating to report — rendering "— ★ / 0 reviews" there reads as
 * "nobody rates this salon" when the truth is "no profile is linked yet".
 * That state gets an explanatory note and a Connect CTA instead; once a
 * connection exists the row falls back to the normal stats presentation,
 * where a genuine 0 is accurate rather than misleading.
 */
function GoogleBusinessPlatformRow() {
  const { t } = useTranslation("reviews");
  const { connection, loading } = useGoogleBusinessConnection();

  // Hold the row back until the connection probe settles so we never
  // flash the "not connected" copy at an already-connected salon.
  if (loading && !connection) return null;

  if (!connection) {
    return (
      <div className="rounded-md border border-border/60 bg-card/50 px-3 py-2.5 space-y-2">
        <p className="text-sm font-medium">{t("overview.googleNotConnectedTitle")}</p>
        <p className="text-xs text-muted-foreground">{t("overview.googleNotConnectedHint")}</p>
        <Button asChild variant="outline" size="sm" className="w-full">
          <Link to="/settings/google-business">{t("overview.connectGoogleBusiness")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <PlatformRow
      source="GOOGLE"
      label={t("overview.googleBusinessCardTitle")}
      href="/customer-service/google-business"
      accent="emerald"
    />
  );
}

/**
 * Empty state for the Customer Feedback card — replaces the stats body
 * while the salon has zero reviews from every source.
 *
 * Kept as its own component rather than inlined so the Google Business
 * connection query only fires on the empty path; salons that already
 * have reviews never pay for it.
 *
 * The CTA follows the same connection gating as `RequestReviewCard`:
 * with a `placeId` we can hand out the review link/QR directly, without
 * one the only useful next step is connecting the profile in settings.
 */
function NoFeedbackPlaceholder() {
  const { t } = useTranslation("reviews");
  const navigate = useNavigate();
  const { connection } = useGoogleBusinessConnection();
  const [dialogOpen, setDialogOpen] = useState(false);

  const placeId = connection?.placeId ?? null;

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <Star className="h-10 w-10 text-slate-400" strokeWidth={1.5} aria-hidden="true" />

        <div className="space-y-1">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {t("overview.noFeedbackTitle")}
          </h3>
          <p className="text-sm font-normal text-slate-500 dark:text-slate-400">
            {t("overview.noFeedbackDescription")}
          </p>
        </div>

        {placeId ? (
          <Button type="button" size="sm" className="mt-1" onClick={() => setDialogOpen(true)}>
            {t("overview.noFeedbackRequestCta")}
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className="mt-1"
            onClick={() => navigate("/settings/google-business")}
          >
            {t("overview.noFeedbackConnectCta")}
          </Button>
        )}
      </div>

      {placeId && (
        <RequestReviewDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          placeId={placeId}
          locationName={connection?.locationName ?? ""}
        />
      )}
    </>
  );
}

interface PlatformRowProps {
  source: SalonReviewSource;
  label: string;
  href: string;
  accent: "indigo" | "emerald";
  /**
   * Drop the row entirely once its source reports zero reviews — used by
   * sources that have nothing to offer when empty. Sources that stay
   * actionable at zero (Google Business, which still links to its synced
   * review page) leave this off.
   */
  hideWhenEmpty?: boolean;
}

function PlatformRow({ source, label, href, accent, hideWhenEmpty }: PlatformRowProps) {
  const { t } = useTranslation("reviews");
  const navigate = useNavigate();
  const { stats, loading } = useSalonReviewStats(source);

  const count = stats?.count ?? 0;

  if (hideWhenEmpty && count === 0 && stats) return null;

  return (
    <button
      type="button"
      onClick={() => navigate(href)}
      className="w-full text-left rounded-md border border-border/60 bg-card/50 hover:border-foreground/20 hover:shadow-sm transition-all px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{label}</p>
        {loading && !stats ? (
          <Spinner size="sm" />
        ) : (
          <div className="flex items-center gap-1.5 mt-0.5">
            <span
              className={cn(
                "text-lg font-semibold tabular-nums",
                accent === "indigo" && "text-indigo-600 dark:text-indigo-400",
                accent === "emerald" && "text-emerald-600 dark:text-emerald-400"
              )}
            >
              {count ? (stats?.averageRating ?? 0).toFixed(1) : "—"}
            </span>
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            <span className="text-xs text-muted-foreground ml-auto">
              {count} {count === 1 ? t("overview.review") : t("overview.reviews")}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          </div>
        )}
      </div>
    </button>
  );
}
